import React, { useState, useEffect, useMemo } from 'react';
import { useRouteMatch, useParams } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Divider, Grid, Typography, withStyles } from '@material-ui/core';
import { OutlinedButton, Tag, BackgroundPaper } from '@ds/react-components';
import Table from 'components/ui/Table';
import { numberToCurrency } from 'utils/misc';
import { groupTransactionsByPaymentId } from 'utils/transactions';
import PageWrapper from 'components/ui/PageWrapper';
import Filter from './Filter';
import tableMetadata from './tableMetaData';

const styles = (theme) => ({
  divider: {
    height: 1,
    color: theme.palette.common.black,
  },
  data: {
    height: 40,
  },
  dataLeft: {
    '& p': {
      color: theme.palette.common.black,
      fontSize: '20px',
    },
  },
  dataRight: {
    '& p': {
      textAlign: 'right',
      fontSize: '16px',
      color: theme.palette.common.black,
      paddingRight: '20px',
    },
  },
  head: {
    backgroundColor: theme.palette.common.white,
    padding: '20px 8px 8px 25px',
    borderRadius: theme.shape.borderRadius,
  },

  nameHead: {
    padding: '20px 0px 30px 0px',
  },
  amountPos: {
    color: theme.palette.common.success,
  },
  amountNeg: {
    color: theme.palette.common.error,
  },
  paymentType: {
    color: theme.palette.common.primary,
  },
  totalPaid: {
    fontWeight: theme.palette.common.boldFont,
  },
});

const Transactions = (props) => {
  const {
    getAllTransactions,
    getAccountInfo,
    transactions,
    downloadTransactions,
    localisation,
    classes,
    resetTransPage,
  } = props;

  const [transactionsState, setTransactionState] = useState({
    currentPage: 0,
    startDate: null,
    endDate: null,
    transactions: {},
    adfitNo: '',
    getTrans: false,
    startDateHelperText: '',
    endDateHelperText: '',
  });

  const match = useRouteMatch();
  const params = useParams();

  useEffect(() => {
    let matchSplit = match.url.split('/');
    let tab = matchSplit.length === 4 ? matchSplit[3] : '';
    setTransactionState((prevState) => ({
      ...prevState,
      tab: tab,
      adfitNo: params.adfitNo,
    }));
    getAccountInfo(params.adfitNo);
    getAllTransactions(params.adfitNo);
  }, [params.adfitNo, match.url, getAccountInfo, getAllTransactions]);

  useEffect(() => {
    return () => {
      resetTransPage();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageClick = (data) => {
    setTransactionState((prevState) => ({
      ...prevState,
      currentPage: data.selected,
    }));
  };

  const checkValidTranDate = (
    momentFromDate,
    momentToDate,
    transactionDate
  ) => {
    const momentTransactionDate = moment(transactionDate);

    const isFromValid =
      momentFromDate && momentFromDate.isValid()
        ? momentTransactionDate.isSameOrAfter(momentFromDate, 'days')
        : true;

    const isToValid =
      momentToDate && momentToDate.isValid()
        ? momentTransactionDate.isSameOrBefore(momentToDate, 'days')
        : true;

    return isFromValid && isToValid;
  };

  const handleFilterClick = () => {
    let transData = transactions.transactions;
    let { accountInfor } = transactions;
    let filteredTrans = [];
    const { startDate, endDate } = transactionsState;

    if (!startDate && !endDate) {
      filteredTrans = transData;
      setTransactionState((prevState) => ({
        ...prevState,
        transactions: {
          transactions: filteredTrans,
          accountInfor: accountInfor,
        },
      }));
      return;
    }

    transData.forEach((tran) => {
      let validTranDate = checkValidTranDate(
        startDate,
        endDate,
        tran.transDate
      );

      if (validTranDate) {
        filteredTrans.push(tran);

        return;
      }
    });

    setTransactionState((prevState) => ({
      ...prevState,
      transactions: { transactions: filteredTrans, accountInfor: accountInfor },
    }));
  };

  const validateDate = (momentDate, dateFormat) => {
    if (!momentDate) return '';
    const formatText = (dateFormat || 'DD/MM/YYYY').toUpperCase();
    return !momentDate.isValid()
      ? `Please enter a valid date ('${formatText}')`
      : '';
  };

  const handleStartDateChange = (date) => {
    let helperText = validateDate(date, localisation.locales.datePickerFormat);
    setTransactionState((prevState) => ({
      ...prevState,
      startDateHelperText: helperText,
      startDate: date,
    }));
  };

  const handleEndDateChange = (date) => {
    let helperText = validateDate(date, localisation.locales.datePickerFormat);
    setTransactionState((prevState) => ({
      ...prevState,
      endDateHelperText: helperText,
      endDate: date,
    }));
  };

  const handleClearClick = () => {
    let transData = transactions.transactions;
    let { accountInfor } = transactions;
    setTransactionState((prevState) => ({
      ...prevState,
      startDate: null,
      endDate: null,
      transactions: { transactions: transData, accountInfor: accountInfor },
    }));
  };

  const handleOnClick = () => {
    downloadTransactions(transactionsState.adfitNo);
  };

  const amountFld = (amount, classes, localisation) => (
    <Typography className={amount < 0 ? classes.amountNeg : classes.amountPos}>
      {amount < 0 ? '-' : '+'}
      {numberToCurrency(Math.abs(amount), localisation)}
    </Typography>
  );

  // eslint-disable-next-line no-prototype-builtins
  const transData = transactionsState.transactions.hasOwnProperty(
    'transactions'
  )
    ? transactionsState.transactions
    : transactions;

  const { name } = Array.isArray(transData.accountInfor)
    ? transData.accountInfor[0]
    : {};
  const { adfitNo } = Array.isArray(transData.accountInfor)
    ? transData.accountInfor[0]
    : {};
  const { total } = Array.isArray(transData.accountInfor)
    ? transData.accountInfor[0]
    : {};

  const trans = Array.isArray(transData.transactions)
    ? transData.transactions
    : [];
  const groupedTransactions = groupTransactionsByPaymentId(trans);
  const createRowItems = (transactions, classes, localisation) => {
    const { dateFormat } = localisation.locales;
    let result = transactions.map((item, index) => {
      // Date
      item.transDateStr = (
        <Typography className={classes.transDate}>
          {moment(item.transDate).format(dateFormat)}
        </Typography>
      );

      // Payment method
      item.paymentTypeStr = (
        <Tag variant="info">
          {localisation.prompts.paymentType[item.paymentType] || 'Other'}
        </Tag>
      );

      // Description
      if (item.isCombinedPayment) {
        item.transcodeStr = 'Combined payment';
        item.__childrens.forEach((childItem) => {
          childItem.transcodeStr =
            localisation.prompts.transactionCode[childItem.transcode];
          childItem.amountFld = amountFld(
            childItem.amount,
            classes,
            localisation
          );
        });
      } else {
        item.transcodeStr =
          localisation.prompts.transactionCode[item.transcode];
      }

      // Amount
      item.amountFld = amountFld(item.amount, classes, localisation);

      // Details
      item.errorCodeStr = localisation.prompts.transErrorCode[item.errorCode];
      if (item.retryCount > 0) {
        item.errorCodeStr = 'Payment Retry';
      }

      return item;
    });
    return result;
  };

  const data = useMemo(
    () => createRowItems(groupedTransactions, classes, localisation),
    [classes, createRowItems, groupedTransactions, localisation, trans]
  );

  const totalAmount =
    total == null ? parseFloat(0).toFixed(2) : parseFloat(total).toFixed(2);
  const displayName = adfitNo ? name + ', ' + adfitNo : '';
  return (
    <PageWrapper>
      <Grid container className={classes.nameHead} justify="space-between">
        <Grid>
          <Typography variant="h3">{displayName}</Typography>
        </Grid>
        <Grid>
          <OutlinedButton onClick={handleOnClick}>
            {'Download All'}
          </OutlinedButton>
        </Grid>
      </Grid>

      <BackgroundPaper>
        <Grid container className={classes.head}>
          <Grid item xs={6} className={classes.dataLeft}>
            <Typography>Transactions</Typography>
          </Grid>

          <Grid item xs={6} className={classes.dataRight}>
            <Typography>
              Total paid <b>${totalAmount}</b>
            </Typography>
          </Grid>
        </Grid>
        <Divider className={classes.divider} />

        <Grid>
          <Filter
            handleClearClick={handleClearClick}
            handleFilterClick={handleFilterClick}
            handleStartDateChange={handleStartDateChange}
            handleEndDateChange={handleEndDateChange}
            startDate={transactionsState.startDate}
            endDate={transactionsState.endDate}
            localisation={localisation}
            startDateHelperText={transactionsState.startDateHelperText}
            endDateHelperText={transactionsState.endDateHelperText}
          />
        </Grid>
      </BackgroundPaper>
      <Table
        data={data}
        metadata={tableMetadata}
        noBackground={false}
        enableSorting={true}
        showFooter={true}
        showEmptyRows={true}
        enableExpandableRow={true}
        expandableColumn={'transcode'}
        currentPage={transactionsState.currentPage}
        onPageChange={handlePageClick}
        footerText={
          <div>
            <b>{trans.length}</b> Results Found
          </div>
        }
      />
    </PageWrapper>
  );
};

Transactions.propTypes = {
  getAllTransactions: PropTypes.func.isRequired,
  getAccountInfo: PropTypes.func.isRequired,
  transactions: PropTypes.object,
  downloadTransactions: PropTypes.func.isRequired,
  localisation: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

export default withStyles(styles)(Transactions);
